/**
 * Dialog **session** identity — the one guard that decides whether an async
 * answer may still touch the UI, and which account a submission targets.
 *
 * round-1 fix (F1 / F2 / F10, category race-condition). `submission.ts` models
 * one dialog *opening* as a state machine, but a state machine cannot by itself
 * answer the two questions a late response raises:
 *
 * 1. **Is this answer still mine?** A request is in flight, the operator closes
 *    the dialog (Escape, Cancel, or a route change) and opens the same dialog
 *    again. Without an identity the old promise resolves into the *new*
 *    opening: it toasts a result the operator did not just ask for, refreshes,
 *    and calls the parent's `onClose`, which closes the freshly opened dialog
 *    and discards its typed input.
 * 2. **Which account is it for?** The account-detail route is reused across
 *    account ids (browser back/forward), so `props.accountId` can change under
 *    an open dialog. A submission that reads the *current* props would post the
 *    amount an operator entered for account A against account B — and a retry
 *    would post it a second time.
 *
 * Both are answered by binding one monotonic `SessionToken` to each opening,
 * freezing `applicationId`/`accountId` into it, and discarding — completely,
 * with no state update and no parent callback — any answer whose token is no
 * longer the controller's current one. Discarding is the whole contract: a
 * stale answer is not re-routed to the new session, because its idempotency key,
 * its payload, and possibly its account all belong to the old one.
 *
 * Immutable by construction: a token is frozen at creation and never mutated;
 * invalidation replaces the controller's reference rather than editing a token.
 */

/** A single dialog opening: its identity plus the target it is bound to. */
export interface SessionToken {
  /** Process-unique, monotonically increasing. Never reused. */
  readonly id: number
  readonly applicationId: number
  readonly accountId: string
}

/** The write target frozen at open time; the only one a submission may use. */
export interface SessionTarget {
  readonly applicationId: number
  readonly accountId: string
}

let nextSessionId = 0

/** Mint a new frozen token for one dialog opening. */
export function openSession(applicationId: number, accountId: string): SessionToken {
  nextSessionId += 1
  return Object.freeze({ id: nextSessionId, applicationId, accountId })
}

/** Whether `token` is still the live session. A `null` current means "closed". */
export function isCurrentSession(
  token: SessionToken,
  current: SessionToken | null
): boolean {
  return current !== null && current.id === token.id
}

/** Whether two write targets are the same one. */
export function sameTarget(a: SessionTarget, b: SessionTarget): boolean {
  return a.applicationId === b.applicationId && a.accountId === b.accountId
}

/** Whether a token is still bound to the props it was opened with. */
export function targetMatches(
  token: SessionToken,
  applicationId: number,
  accountId: string
): boolean {
  return sameTarget(token, { applicationId, accountId })
}

/** The frozen target of a session — never read from live props. */
export function sessionTarget(token: SessionToken): SessionTarget {
  return { applicationId: token.applicationId, accountId: token.accountId }
}

/**
 * Holds the live token for one mounted dialog. Deliberately plain (not React
 * state): the guard must be readable from inside an already-running async
 * callback, where a re-rendered state value would be stale.
 */
export interface SessionController {
  /** Start a new opening, replacing (and thereby invalidating) any previous. */
  open(applicationId: number, accountId: string): SessionToken
  current(): SessionToken | null
  /** Close: every in-flight answer of the current session is now discardable. */
  invalidate(): void
  isCurrent(token: SessionToken): boolean
}

export function createSessionController(): SessionController {
  let current: SessionToken | null = null
  return {
    open(applicationId: number, accountId: string): SessionToken {
      current = openSession(applicationId, accountId)
      return current
    },
    current: () => current,
    invalidate() {
      current = null
    },
    isCurrent: (token: SessionToken) => isCurrentSession(token, current),
  }
}

/**
 * The body of a dialog's session effect: open one session and hand back the
 * cleanup that invalidates it.
 *
 * round-2 fix (F1, category race-condition). Opening a session used to be a
 * render-time side effect while invalidation was an effect cleanup — an
 * asymmetry React's StrictMode punishes: it mounts, runs every cleanup, then
 * runs every effect again. The cleanup killed the session and nothing minted a
 * replacement, so every request already in flight was discarded and any
 * one-shot effect that had already fired never fired again. Opening and
 * invalidating are therefore the two halves of a single effect: each mount —
 * including StrictMode's replay — ends with exactly one live session, and
 * `onSessionId` publishes that identity so effects that must run *per session*
 * can depend on it.
 *
 * `onSessionId(null)` on cleanup is deliberate: "no live session" is a state
 * the UI can observe, not something it has to infer.
 */
export function openSessionEffect(
  controller: SessionController,
  applicationId: number,
  accountId: string,
  onSessionId: (sessionId: number | null) => void
): () => void {
  const token = controller.open(applicationId, accountId)
  onSessionId(token.id)
  return () => {
    controller.invalidate()
    onSessionId(null)
  }
}

export type SendDisposition = 'applied' | 'discarded'

export interface SendInSessionArgs<T> {
  token: SessionToken
  controller: Pick<SessionController, 'isCurrent'>
  /** Receives the session's frozen target — never the component's live props. */
  request: (target: SessionTarget) => Promise<T>
  onSuccess: (value: T) => void
  onFailure: (error: unknown) => void
}

/**
 * Run one submission under a session token and apply its answer **only** while
 * that session is still current.
 *
 * A discarded answer produces no state update, no toast, no refresh, and no
 * parent callback — neither on success nor on failure. A discarded *success* is
 * not lost data: the write did land server-side and the reopened dialog's
 * refresh (or the operator's own ledger read) shows it; what must not happen is
 * a callback from a dead session reaching into the live one.
 */
export async function sendInSession<T>(
  args: SendInSessionArgs<T>
): Promise<SendDisposition> {
  const { token, controller, request, onSuccess, onFailure } = args
  let value: T
  try {
    value = await request(sessionTarget(token))
  } catch (error: unknown) {
    if (!controller.isCurrent(token)) return 'discarded'
    onFailure(error)
    return 'applied'
  }
  if (!controller.isCurrent(token)) return 'discarded'
  onSuccess(value)
  return 'applied'
}
